/**
 * 매핑 제안용 타입·값 메타 — 자료형 문자열 파싱과 샘플 값 프로파일링.
 *
 * 매핑 후보를 "타입이 같다" 로 만들면 후보가 폭발한다(LiveSQL 실측: 도메인 매칭 11,464건).
 * 그래서 여기서 얻는 정보는 **후보를 만드는 데 쓰지 않고, 만들어진 후보를 검증·경고하는 데만** 쓴다.
 */

export type BaseType = 'VARCHAR' | 'INTEGER' | 'NUMERIC' | 'DATE' | 'TIMESTAMP' | 'BOOLEAN' | 'BINARY' | 'UNKNOWN';

export interface TypeMeta {
  base: BaseType;
  /** 문자 길이 또는 숫자 전체 자릿수 (모르면 undefined) */
  length?: number;
  /** 소수 자릿수 */
  scale?: number;
  raw: string;
}

const BASE_PATTERNS: Array<[RegExp, BaseType]> = [
  [/^(varchar2?|character varying|char|nchar|nvarchar2?|text|clob|string)$/i, 'VARCHAR'],
  [/^(int|int2|int4|int8|integer|smallint|bigint|serial|bigserial|number)$/i, 'INTEGER'],
  [/^(numeric|decimal|dec|float|float4|float8|double precision|double|real|money)$/i, 'NUMERIC'],
  [/^(date)$/i, 'DATE'],
  [/^(timestamp.*|datetime|time)$/i, 'TIMESTAMP'],
  [/^(bool|boolean)$/i, 'BOOLEAN'],
  [/^(bytea|blob|raw|binary|varbinary|image)$/i, 'BINARY'],
];

/**
 * 자료형 문자열 → 기준 타입·길이. PostgreSQL(`character varying(50)`), Oracle(`VARCHAR2(20 BYTE)`),
 * MySQL(`decimal(12,2)`), 데이터 정의의 단순 표기(`VARCHAR`)를 모두 받는다.
 */
export function parseDataType(dataType?: string): TypeMeta {
  const raw = (dataType || '').trim();
  if (!raw) return { base: 'UNKNOWN', raw };
  // 괄호 기준으로 이름과 인자를 가른다 — VARCHAR2·INT4 처럼 이름이 숫자로 끝나는 타입도 안전하다
  const lp = raw.indexOf('(');
  const namePart = (lp >= 0 ? raw.slice(0, lp) : raw).trim();
  const args = lp >= 0 ? raw.slice(lp + 1, raw.lastIndexOf(')') > lp ? raw.lastIndexOf(')') : undefined) : '';
  // '20 BYTE' · '12,2' → 숫자만 추린다
  const nums = (args.match(/\d+/g) || []).map(Number);
  const length = nums.length > 0 ? nums[0] : undefined;
  const scale = nums.length > 1 ? nums[1] : undefined;
  let base: BaseType = 'UNKNOWN';
  for (const [re, b] of BASE_PATTERNS) {
    if (re.test(namePart)) { base = b; break; }
  }
  // NUMBER(10) 처럼 소수부가 없으면 정수로 본다 (Oracle)
  if (base === 'INTEGER' && scale !== undefined && scale > 0) base = 'NUMERIC';
  return { base, length, scale, raw };
}

/** 타입 계열이 서로 담을 수 있는지 — 경고 판정용 */
export function typeCompatible(src: BaseType, tgt: BaseType): 'ok' | 'check' | 'bad' {
  if (src === 'UNKNOWN' || tgt === 'UNKNOWN') return 'ok';       // 모르면 트집 잡지 않는다
  if (src === tgt) return 'ok';
  const num = (t: BaseType) => t === 'INTEGER' || t === 'NUMERIC';
  const time = (t: BaseType) => t === 'DATE' || t === 'TIMESTAMP';
  if (num(src) && num(tgt)) return 'ok';
  if (time(src) && time(tgt)) return 'ok';
  if (tgt === 'VARCHAR') return 'ok';                             // 무엇이든 문자로는 담긴다
  if (src === 'VARCHAR' && (num(tgt) || time(tgt) || tgt === 'BOOLEAN')) return 'check';  // 값에 달렸다
  if (src === 'BINARY' || tgt === 'BINARY') return 'bad';
  return 'check';
}

export type ValuePattern =
  | 'date:YYYYMMDD' | 'date:YYYY-MM-DD' | 'date:YYYY/MM/DD' | 'datetime'
  | 'number:plain' | 'number:comma' | 'yn' | 'code' | 'text' | 'empty';

export interface ValueProfile {
  pattern: ValuePattern;
  /** 값의 최대 길이(문자 수) */
  maxLen: number;
  /** 값이 비어 있던 비율 0~1 */
  nullRate: number;
  /** 서로 다른 값 (최대 20개까지만) — 코드값 판정용 */
  distinct: string[];
  /** 앞뒤 공백이 있는 값이 있었나 (고정폭 파일에서 흔하다) */
  padded: boolean;
}

const RE_YYYYMMDD = /^\d{8}$/;
const RE_DASH = /^\d{4}-\d{2}-\d{2}$/;
const RE_SLASH = /^\d{4}\/\d{2}\/\d{2}$/;
const RE_DATETIME = /^\d{4}[-/]\d{2}[-/]\d{2}[ T]\d{2}:\d{2}/;
const RE_NUM = /^[-+]?\d+(\.\d+)?$/;
const RE_NUM_COMMA = /^[-+]?\d{1,3}(,\d{3})+(\.\d+)?$/;

/** 샘플 값 목록 → 패턴·길이·널비율. 값이 없으면 'empty' */
export function profileValues(values: Array<unknown>): ValueProfile {
  const raw = values.map((v) => (v === null || v === undefined ? '' : String(v)));
  const nonEmpty = raw.filter((s) => s.trim() !== '');
  const nullRate = raw.length ? (raw.length - nonEmpty.length) / raw.length : 0;
  const padded = raw.some((s) => s !== s.trim() && s.trim() !== '');
  const vals = nonEmpty.map((s) => s.trim());
  const distinct = Array.from(new Set(vals)).slice(0, 20);
  const maxLen = vals.reduce((a, s) => Math.max(a, s.length), 0);
  if (!vals.length) return { pattern: 'empty', maxLen: 0, nullRate, distinct: [], padded };

  const all = (re: RegExp) => vals.every((s) => re.test(s));
  // 8자리 숫자는 날짜일 수도 그냥 숫자일 수도 있다 — 년도 범위로 가른다
  const looksDate8 = all(RE_YYYYMMDD) && vals.every((s) => {
    const y = Number(s.slice(0, 4)); const mo = Number(s.slice(4, 6)); const d = Number(s.slice(6, 8));
    return y >= 1900 && y <= 2999 && mo >= 1 && mo <= 12 && d >= 1 && d <= 31;
  });
  let pattern: ValuePattern = 'text';
  if (all(RE_DATETIME)) pattern = 'datetime';
  else if (all(RE_DASH)) pattern = 'date:YYYY-MM-DD';
  else if (all(RE_SLASH)) pattern = 'date:YYYY/MM/DD';
  else if (looksDate8) pattern = 'date:YYYYMMDD';
  else if (all(RE_NUM_COMMA)) pattern = 'number:comma';
  // '01','02' 처럼 앞자리 0 이 있는 짧은 값은 숫자가 아니라 코드다 (숫자로 넣으면 0 이 사라진다)
  else if (all(RE_NUM) && vals.every((s) => /^0\d/.test(s)) && maxLen <= 6 && distinct.length <= 20) pattern = 'code';
  else if (all(RE_NUM)) pattern = 'number:plain';
  else if (vals.every((s) => /^[YN]$/i.test(s))) pattern = 'yn';
  else if (distinct.length <= 10 && maxLen <= 10 && vals.length >= distinct.length * 2) pattern = 'code';
  return { pattern, maxLen, nullRate, distinct, padded };
}

/** 날짜 계열 패턴인가 */
export function isDatePattern(p: ValuePattern): boolean {
  return p === 'date:YYYYMMDD' || p === 'date:YYYY-MM-DD' || p === 'date:YYYY/MM/DD' || p === 'datetime';
}
